// src/hooks/useSidebarOrder.ts
// Хук для сохранения порядка пунктов сайдбара (drag-n-drop)
import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/client';

export function useSidebarOrder<T extends { path: string }>(
  moduleKey: string,
  defaultItems: T[]
): {
  items: T[];
  reorder: (fromIndex: number, toIndex: number) => void;
  reset: () => void;
  saving: boolean;
} {
  const [items, setItems] = useState<T[]>(defaultItems);
  const [saving, setSaving] = useState(false);
  const loadedRef = useRef(false);

  // ─── Загрузка из сервера / localStorage ───
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;

    (async () => {
      // 1. Сразу применяем из localStorage (мгновенно)
      try {
        const cached = localStorage.getItem(`sidebar_order_${moduleKey}`);
        if (cached) {
          const order: string[] = JSON.parse(cached);
          applyOrder(defaultItems, order);
        }
      } catch (e) { /* ignore */ }

      // 2. Потом тянем с сервера (актуальнее)
      try {
        const r = await api.get('/users/preferences');
        const order = r.data?.preferences?.sidebarOrder?.[moduleKey];
        if (Array.isArray(order)) {
          applyOrder(defaultItems, order);
          try { localStorage.setItem(`sidebar_order_${moduleKey}`, JSON.stringify(order)); } catch (e) {}
        }
      } catch (e) { /* ignore */ }
    })();

    function applyOrder(list: T[], order: string[]) {
      // Сортируем по order + добавляем новые пункты в конец
      const ordered: T[] = [];
      const used = new Set<string>();

      for (const path of order) {
        const found = list.find(i => i.path === path);
        if (found) {
          ordered.push(found);
          used.add(path);
        }
      }
      // Добавляем новые (которые появились после сохранения)
      for (const item of list) {
        if (!used.has(item.path)) ordered.push(item);
      }
      setItems(ordered);
    }
  }, [moduleKey, defaultItems]);

  // ─── Сохранение ───
  const saveOrder = useCallback(async (newItems: T[]) => {
    const order = newItems.map(i => i.path);
    // 1. Мгновенно в localStorage
    try { localStorage.setItem(`sidebar_order_${moduleKey}`, JSON.stringify(order)); } catch (e) {}

    // 2. Асинхронно на сервер
    setSaving(true);
    try {
      await api.patch('/users/preferences', {
        sidebarOrder: { [moduleKey]: order },
      });
    } catch (e) { /* ignore */ }
    finally { setSaving(false); }
  }, [moduleKey]);

  // ─── Reorder ───
  const reorder = useCallback((fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    setItems(prev => {
      const next = [...prev];
      const [removed] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, removed);
      saveOrder(next);
      return next;
    });
  }, [saveOrder]);

  // ─── Reset ───
  const reset = useCallback(() => {
    setItems(defaultItems);
    try { localStorage.removeItem(`sidebar_order_${moduleKey}`); } catch (e) {}
    saveOrder(defaultItems);
  }, [defaultItems, moduleKey, saveOrder]);

  return { items, reorder, reset, saving };
}

export default useSidebarOrder;
